import { openDB, type DBSchema, type IDBPDatabase } from "idb"

import {
  DRAFT_MAX_AGE_MS,
  DRAFTS_PER_PROJECT_LIMIT,
  type DraftKind,
  type DraftSaveResult,
  type FormDraft,
} from "./types"

export const DRAFTS_CHANGED_EVENT = "fieldbook:drafts-changed"

function notifyDraftsChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(DRAFTS_CHANGED_EVENT))
  }
}

interface DraftsDb extends DBSchema {
  drafts: {
    key: string
    value: FormDraft
    indexes: {
      resumeKey: string
      updatedAt: number
      projectId: string
      ownerId: string
    }
  }
}

const DB_NAME = "form-drafts"
const DB_VERSION = 3

let dbPromise: Promise<IDBPDatabase<DraftsDb>> | null = null

function getDb() {
  dbPromise ??= openDB<DraftsDb>(DB_NAME, DB_VERSION, {
    upgrade(db, _oldVersion, _newVersion, transaction) {
      // Add missing indexes without replacing the store, preserving both
      // recovery records and user-saved drafts across schema upgrades.
      const store = db.objectStoreNames.contains("drafts")
        ? transaction.objectStore("drafts")
        : db.createObjectStore("drafts", { keyPath: "id" })

      if (!store.indexNames.contains("resumeKey")) {
        store.createIndex("resumeKey", "resumeKey")
      }
      if (!store.indexNames.contains("updatedAt")) {
        store.createIndex("updatedAt", "updatedAt")
      }
      if (!store.indexNames.contains("projectId")) {
        store.createIndex("projectId", "projectId")
      }
      if (!store.indexNames.contains("ownerId")) {
        store.createIndex("ownerId", "ownerId")
      }
    },
    // Another tab (or a test) upgrading or deleting the database can't
    // proceed while this connection is open, so step aside for it.
    blocking() {
      void dbPromise?.then((db) => db.close())
      dbPromise = null
    },
  })
  // A rejected open (a one-off blocked upgrade, a transient browser error)
  // would otherwise stick around forever — every future call reuses the same
  // dead promise via the caching above, so one bad open would silently
  // disable drafts for the rest of the session. Clearing it here lets the
  // next attempt try fresh instead of replaying history.
  dbPromise.catch(() => {
    dbPromise = null
  })
  return dbPromise
}

/**
 * Whether a failed write looks like the device actually being out of space,
 * as opposed to some other reason IndexedDB refused it (disabled storage, a
 * blocked connection, an unexpected error). Only this case is safe to tell
 * the user "storage may be full" — anything else would be a guess.
 */
function isQuotaExceeded(error: unknown): boolean {
  return error instanceof DOMException && error.name === "QuotaExceededError"
}

export type SaveDraftResult = {
  saved: boolean
  /** Meaningful only when `saved` is false. */
  quotaExceeded: boolean
}

/**
 * Drafts are a convenience layered over the real save path, so every operation
 * degrades to a no-op rather than taking a form down with it. A WebView with
 * storage disabled, a quota failure mid-survey, a blocked upgrade — none of
 * those should stop someone recording an observation.
 */
async function withDb<T>(
  operation: (db: IDBPDatabase<DraftsDb>) => Promise<T>,
  fallback: T,
  context: string,
): Promise<T> {
  try {
    return await operation(await getDb())
  } catch (error) {
    console.warn(`[Drafts] ${context} failed`, error)
    return fallback
  }
}

export async function loadDraft(
  id: string,
  ownerId?: string,
): Promise<FormDraft | null> {
  return withDb(
    async (db) => {
      const draft = await db.get("drafts", id)
      if (!draft) return null
      return !ownerId || draft.ownerId === ownerId ? draft : null
    },
    null,
    `loadDraft(${id})`,
  )
}

/**
 * The temporary recovery record a form should restore after a crash. Drafts
 * explicitly saved by the user are opened only from the project launcher, so
 * starting another form of the same shape remains blank.
 */
export async function loadResumableDraft(
  resumeKey: string,
  ownerId: string,
): Promise<FormDraft | null> {
  return withDb(
    async (db) => {
      const matches = (
        await db.getAllFromIndex("drafts", "resumeKey", resumeKey)
      ).filter((draft) => !draft.savedByUser && draft.ownerId === ownerId)
      if (matches.length === 0) return null
      return matches.reduce((newest, draft) =>
        draft.updatedAt > newest.updatedAt ? draft : newest,
      )
    },
    null,
    `loadResumableDraft(${resumeKey})`,
  )
}

/**
 * Persists a user-visible draft, enforcing the per-user/project limit.
 */
export async function saveUserDraft(
  draft: FormDraft,
): Promise<DraftSaveResult> {
  const result = await withDb<DraftSaveResult>(
    async (db) => {
      const transaction = db.transaction("drafts", "readwrite")
      const existing = await transaction.store.get(draft.id)

      if (!existing?.savedByUser) {
        const projectDrafts = await transaction.store
          .index("projectId")
          .getAll(draft.projectId)
        const projectDraftCount = projectDrafts.filter(
          (candidate) =>
            candidate.savedByUser && candidate.ownerId === draft.ownerId,
        ).length
        if (projectDraftCount >= DRAFTS_PER_PROJECT_LIMIT) {
          await transaction.done
          return "limit-reached"
        }
      }

      await transaction.store.put({ ...draft, savedByUser: true })
      await transaction.done
      return "saved"
    },
    "failed",
    `saveUserDraft(${draft.id})`,
  )

  if (result === "saved") notifyDraftsChanged()
  return result
}

/**
 * Autosave/camera snapshots are never subject to the explicit draft limit.
 *
 * @returns `saved: false` when the write did not land — a full or unavailable
 * IndexedDB. Callers about to do something that depends on the draft existing
 * (opening the camera) need to know rather than assume.
 */
export async function saveDraftSnapshot(
  draft: FormDraft,
): Promise<SaveDraftResult> {
  try {
    await (await getDb()).put("drafts", draft)
    notifyDraftsChanged()
    return { saved: true, quotaExceeded: false }
  } catch (error) {
    console.warn(`[Drafts] saveDraftSnapshot(${draft.id}) failed`, error)
    return { saved: false, quotaExceeded: isQuotaExceeded(error) }
  }
}

export async function deleteDraft(id: string): Promise<void> {
  const deleted = await withDb(
    async (db) => {
      await db.delete("drafts", id)
      return true
    },
    false,
    `deleteDraft(${id})`,
  )
  if (deleted) notifyDraftsChanged()
}

/** Lists drafts newest first, optionally narrowed to a project or kind. */
export async function listDrafts(
  filter: {
    projectId?: string
    kind?: DraftKind
    ownerId?: string
    savedByUser?: boolean
  } = {},
): Promise<Array<FormDraft>> {
  return withDb(
    async (db) => {
      const all = filter.projectId
        ? await db.getAllFromIndex("drafts", "projectId", filter.projectId)
        : await db.getAll("drafts")
      return all
        .filter(
          (draft) =>
            (!filter.kind || draft.kind === filter.kind) &&
            (!filter.ownerId || draft.ownerId === filter.ownerId) &&
            (filter.savedByUser === undefined ||
              Boolean(draft.savedByUser) === filter.savedByUser),
        )
        .sort((a, b) => b.updatedAt - a.updatedAt)
    },
    [],
    "listDrafts",
  )
}

/**
 * The draft that was mid-capture when the app went away, if any.
 *
 * Used to reattach a photo the OS held for us across a process death: the
 * capture belongs to whichever form was waiting on it, and only one form can
 * be waiting at a time.
 */
export async function findDraftAwaitingCapture(): Promise<FormDraft | null> {
  return withDb(
    async (db) => {
      const all = await db.getAll("drafts")
      const waiting = all.filter((draft) => draft.captureInFlight)
      if (waiting.length === 0) return null
      return waiting.reduce((newest, draft) =>
        draft.updatedAt > newest.updatedAt ? draft : newest,
      )
    },
    null,
    "findDraftAwaitingCapture",
  )
}

/**
 * Drops stale crash-recovery records. Explicit user saves persist until the
 * user submits or deletes them.
 */
export async function pruneStaleDrafts(
  maxAgeMs = DRAFT_MAX_AGE_MS,
): Promise<number> {
  return withDb(
    async (db) => {
      const cutoff = Date.now() - maxAgeMs
      const candidates = await db.getAllFromIndex(
        "drafts",
        "updatedAt",
        IDBKeyRange.upperBound(cutoff),
      )
      const stale = candidates
        .filter((draft) => !draft.savedByUser)
        .map((draft) => draft.id)
      await Promise.all(stale.map((key) => db.delete("drafts", key)))
      return stale.length
    },
    0,
    "pruneStaleDrafts",
  )
}

/**
 * Test seam: closes the connection and forces the next call to reopen. The
 * close matters — an open connection blocks deleteDatabase indefinitely.
 */
export async function resetDraftsDbForTests() {
  const pending = dbPromise
  dbPromise = null
  if (!pending) return
  try {
    ;(await pending).close()
  } catch {
    // Already closed, or never opened successfully.
  }
}
